import { useState } from 'react';

import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Minus, Plus } from 'lucide-react';

import { useConfirmModal } from '@/hooks/useConfirmModal';

import { Button } from './ui/button';
import { Card, CardHeader } from './ui/card';
import { Textarea } from './ui/textarea';

const SortableCard = ({ id, children }) => {
  const { setNodeRef, attributes, listeners, transform, transition } =
    useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style}>
      {children({ dragHandleProps: { ...attributes, ...listeners } })}
    </div>
  );
};

const MultiText = ({
  fieldValue,
  handleFieldChange,
  id,
  mode = 'edit',
  placeholder,
}) => {
  const [items, setItems] = useState(
    fieldValue || [
      {
        id: +new Date(),
        order: 0,
        value: '',
      },
    ]
  );
  const { showConfirmModal } = useConfirmModal();

  const isEdit = mode !== 'static';

  const updateIntoItems = (nextItems) => {
    setItems(nextItems);
    handleFieldChange(id, nextItems);
  };

  const handleItemValueChange = (itemId, target) => {
    updateIntoItems(
      items.map((item) =>
        item.id === itemId ? { ...item, value: target.value } : item
      )
    );
  };

  const handleAddItem = () => {
    updateIntoItems([
      ...items,
      {
        id: +new Date(),
        order: items.length,
        value: '',
      },
    ]);
  };

  const handleDeleteItem = (itemId) => {
    showConfirmModal({
      confirmText: 'OK',
      description: `Are you sure you want to delete this responsibility?`,
      onConfirm: () => {
        updateIntoItems(items.filter((item) => item.id !== itemId));
      },
      title: 'Delete Responsibility',
    });
  };

  const sensors = useSensors(useSensor(PointerSensor));

  return (
    <>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={({ active, over }) => {
          if (!over || active.id === over.id) return;

          const oldIndex = items.findIndex((item) => item.id === active.id);
          const newIndex = items.findIndex((item) => item.id === over.id);
          const reordered = arrayMove(items, oldIndex, newIndex);

          updateIntoItems(reordered);
        }}
      >
        <SortableContext items={items} strategy={verticalListSortingStrategy}>
          <div className="flex flex-col gap-4">
            {items.map((item) => (
              <SortableCard id={item.id} key={item.id}>
                {({ dragHandleProps }) => (
                  <Card>
                    <CardHeader className="flex flex-row gap-4">
                      {isEdit && (
                        <Button
                          variant="ghost"
                          size="icon"
                          {...dragHandleProps}
                        >
                          <GripVertical />
                        </Button>
                      )}
                      {isEdit ? (
                        <Textarea
                          autoResize
                          className="font-mono resize-none"
                          onChange={({ target }) =>
                            handleItemValueChange(item.id, target)
                          }
                          placeholder={
                            placeholder || `Enter content for this section...`
                          }
                          value={item.value}
                        />
                      ) : (
                        item.value
                      )}
                      {isEdit && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteItem(item.id)}
                        >
                          <Minus />
                        </Button>
                      )}
                    </CardHeader>
                  </Card>
                )}
              </SortableCard>
            ))}
          </div>
        </SortableContext>
      </DndContext>
      {isEdit && (
        <div className="flex gap-4 mt-4">
          <Button variant="outline" onClick={handleAddItem}>
            <Plus />
          </Button>
        </div>
      )}
    </>
  );
};

export default MultiText;
